"use client";

import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

/** Characters above which the first paint is clamped before measurement runs. */
export const COLLAPSE_HINT_LENGTH = 220;

/**
 * Pure helper: should the description start collapsed before we can measure
 * the DOM? Used for the first (static) paint so long texts do not flash open.
 */
export function shouldStartCollapsed(text: string, hintLength: number = COLLAPSE_HINT_LENGTH): boolean {
  const trimmed = text.trim();
  if (trimmed.length > hintLength) return true;
  return trimmed.split(/\r?\n/).filter((line) => line.trim().length > 0).length > 3;
}

export interface CollapsibleDescriptionProps {
  text: string;
  /** Visible lines while collapsed. Default 3 (same as mobile). */
  lines?: number;
  readMoreLabel: string;
  readLessLabel: string;
  className?: string;
}

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Event description clamped to `lines` with a fade at the bottom and a
 * "read more" chevron row, mirroring the mobile `CollapsibleDescription`.
 * The toggle only renders when the text actually overflows the clamp (measured
 * after mount, re-measured on resize). Expanding reveals the full text.
 */
export function CollapsibleDescription({
  text,
  lines = 3,
  readMoreLabel,
  readLessLabel,
  className,
}: CollapsibleDescriptionProps) {
  const bodyId = useId();
  const bodyRef = useRef<HTMLParagraphElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(() => shouldStartCollapsed(text));

  useIsomorphicLayoutEffect(() => {
    const node = bodyRef.current;
    if (!node) return;

    const measure = () => {
      // While expanded there is no clamp to measure against; keep the toggle.
      if (expanded) return;
      const fits = node.scrollHeight <= node.clientHeight + 1;
      // jsdom reports 0/0; keep the heuristic when layout is unavailable.
      if (node.clientHeight === 0 && node.scrollHeight === 0) return;
      setOverflows(!fits);
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [text, lines, expanded]);

  const collapsed = overflows && !expanded;

  return (
    <div className={["gruv-collapsible", collapsed ? "gruv-collapsible--collapsed" : "", className ?? ""].join(" ").trim()}>
      <p
        id={bodyId}
        ref={bodyRef}
        className="gruv-event__text gruv-collapsible__body"
        style={collapsed ? ({ WebkitLineClamp: lines } as CSSProperties) : undefined}
        data-testid="collapsible-description"
      >
        {text}
      </p>
      {overflows ? (
        <button
          type="button"
          className="gruv-collapsible__toggle"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={() => setExpanded((value) => !value)}
        >
          <span>{expanded ? readLessLabel : readMoreLabel}</span>
          <svg
            className="gruv-collapsible__chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}
